import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Endpoints } from '@/api';
import { useFetch } from '@/utils/fetch';

export type Brand = Endpoints['GET /brands']['response']['brands'][number];
export type BrandWithRelations = Endpoints['GET /brands/:id']['response'];

export const useBrand = (id?: string) => {
  const fetch = useFetch<Endpoints['GET /brands/:id']>();

  return useQuery({
    queryKey: ['brands', id],
    queryFn: () => fetch(`/brands/${id}`),
    enabled: !!id,
  });
};

export const useBrands = (
  params: Endpoints['GET /brands']['query'],
  options: {
    keepPreviousData?: boolean;
    refetchOnMount?: boolean;
  } = {}
) => {
  const fetch = useFetch<Endpoints['GET /brands']>();

  return useQuery({
    queryKey: ['brands', params],
    queryFn: () => fetch('/brands', params),
    placeholderData: options.keepPreviousData ? keepPreviousData : undefined,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnMount: !!options.refetchOnMount,
  });
};

export const useUpdateBrand = (brandId?: string) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['PATCH /brands/:id']>();

  return useMutation({
    mutationFn: (body: Endpoints['PATCH /brands/:id']['body']) =>
      fetch(`/brands/${brandId}`, undefined, { method: 'PATCH', body }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['brands'] }),
  });
};

export const useDeleteBrand = (brandId: string) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['DELETE /brands/:id']>();

  return useMutation({
    mutationFn: () => fetch(`/brands/${brandId}`, undefined, { method: 'DELETE' }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['brands'] }),
  });
};

/**
 *
 * @param brandToCompare The brand we want to compare to other brands
 * @param brands The list of the other brands
 * @returns The brand + the organization name between parenthesis if other brands share the same name. Ex: "Common Name (Bocage)"
 */
export const computeBrandName = (brandToCompare: Brand, brands: Brand[]): string => {
  if (hasBrandDuplicates(brandToCompare, brands)) {
    return `${brandToCompare.name} (${brandToCompare.organizationName})`;
  } else {
    return brandToCompare.name;
  }
};

/**
 *
 * @param brandToCompare The brand we want to compare to other brands
 * @param brands The list of the other brands
 * @returns True if at least one of the other brand shares the same name
 */
const hasBrandDuplicates = (brandToCompare: Brand, brands: Brand[]): boolean => {
  const currentBrandAndDuplicates = brands.filter((brand) =>
    areBrandsDuplicates(brandToCompare, brand)
  );

  return currentBrandAndDuplicates.length > 1;
};

/**
 *
 * @returns Returns true if both brands share the same name
 */
const areBrandsDuplicates = (brand1: Brand, brand2: Brand): boolean => {
  return brand1.name === brand2.name;
};
